import axios from 'axios';
import { getAccessToken } from '../../utils';
import { isSafeExternalUrl } from '../../utils/safeNavigation';
import { getConnectHubBaseUrl } from '../../utils/connectHubHandoff';

/**
 * Unified Platform communities (IMP-2081).
 *
 * The Messages Communities card reads the signed-in member's community from
 * the Unified Platform. UP mints no tokens of its own — it verifies this app's
 * JWT with the shared key, so the same credential IMP-2065 hands to the hub
 * works here unchanged.
 *
 * Verified against production 2026-09-10: 200 in ~250ms, and an empty array
 * for a member with no community. An empty array is also how UP reports an
 * ineligible partner, cohort or user, so it is the only eligibility check the
 * card needs.
 *
 * DELIBERATELY NOT axiosClient. Four reasons, all measured or read from
 * src/api/axiosClient.js:
 *
 *  1. UP requires `Authorization: Bearer`. axiosClient's request interceptor
 *     always sets `x-auth-token` instead, which UP answers with a hard 401
 *     ("Missing or malformed Authorization header"). Confirmed by request.
 *  2. That 401 would then set axiosClient's module-level `isErrorOccurred`,
 *     after which every later platform request rejects with a fabricated 401
 *     until the page reloads. One UP outage would break the whole app.
 *  3. It would also raise the "session expired" dialog for a failure in a
 *     different system.
 *  4. Its default timeout is 10 minutes. A hung UP request would leave the
 *     card missing for that long with no signal.
 *
 * So this client has no interceptors and a short timeout. It must stay that
 * way; adding interceptors here re-creates the coupling above.
 */

/** Long enough for a slow response, short enough not to strand the card. */
export const REQUEST_TIMEOUT_MS = 8000;

const upClient = axios.create({ timeout: REQUEST_TIMEOUT_MS });

/** UP serves its API and the hub from one host, so one env var configures both. */
export const getCommunitiesUrl = () => {
  const base = getConnectHubBaseUrl();
  if (!isSafeExternalUrl(base)) return null;
  return `${base.replace(/\/+$/, '')}/api/me/communities`;
};

/**
 * True when `url` sits on the configured Connect hub origin.
 *
 * `isSafeExternalUrl` only checks the scheme, so it passes **any** https host.
 * That is the right test for navigation, and the wrong one for a credentialed
 * request. Origins are compared after a URL parse so port, default-port and
 * case normalization are the parser's problem rather than a regex's.
 *
 * @param {string} url
 * @returns {boolean}
 */
const isOnConnectHub = url => {
  const base = getConnectHubBaseUrl();
  if (!isSafeExternalUrl(url) || !isSafeExternalUrl(base)) return false;
  return new URL(url).origin === new URL(base).origin;
};

/**
 * Builds the avatar URL on the Connect hub, or null.
 *
 * UP returns `avatarUrl` as a **path** on its own API — `/api/communities/<id>/
 * avatar?v=<n>` — not an absolute URL. A root-relative path in an `<img src>`
 * resolves against *this* app's origin, so it would silently request
 * `https://pilot.imentor.org/api/...` and 404. Joining it to the hub host is
 * the ordinary job of this function.
 *
 * An already-absolute URL passes through **only when it is on the hub origin**,
 * so UP can change the shape without a deploy here.
 *
 * The pin is the security boundary, not a tidiness rule. `fetchAvatarBlob`
 * sends the member's platform JWT to whatever this returns, and `avatarUrl`
 * arrives in a response from another system. Without the pin, an off-hub
 * absolute value — a CDN, a staff-set field, a poisoned payload — would make
 * the browser send that credential to a third-party origin, which could then
 * read it from a header or retain it in an access log. One origin can receive
 * the token: the one the token is already minted for.
 *
 * Off-hub values fail closed and the card shows the iMentor mark. Nothing is
 * lost today — UP returns paths — and a real CDN avatar would need a route
 * that does not carry the credential. Cursor security review, PR #271.
 *
 * @param {string} ref - A sanitized reference from `sanitizeAvatarUrl`.
 * @returns {string | null}
 */
export const getAvatarUrl = ref => {
  if (typeof ref !== 'string' || !ref) return null;

  const base = getConnectHubBaseUrl();
  if (!isSafeExternalUrl(base)) return null;

  const url = isSafeExternalUrl(ref) ? ref : `${base.replace(/\/+$/, '')}/${ref.replace(/^\/+/, '')}`;

  return isOnConnectHub(url) ? url : null;
};

export const communityService = {
  /**
   * Fetches the member's communities.
   *
   * @returns {Promise<object|null>} The response body, or null when the host
   *   is unconfigured or no session token is available. Callers treat null and
   *   a rejection identically — the card hides either way.
   */
  async fetchMyCommunities() {
    const url = getCommunitiesUrl();
    const token = getAccessToken();
    if (url === null || typeof token !== 'string' || !token) return null;

    // An admin LoginAs token is sent too, on purpose. It is minted for the
    // member, so Connect answers with the member's communities and the admin
    // sees the same card the member does. `url` is always on the configured
    // hub origin, so the token goes nowhere else.
    const response = await upClient.get(url, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    return response?.data ?? null;
  },

  /**
   * Fetches a community avatar as a Blob, or null.
   *
   * The avatar endpoint requires the member's bearer token — measured: no
   * credential returns 401, and an `<img>` element cannot send an
   * Authorization header. So the image is fetched here and the caller turns it
   * into an object URL.
   *
   * Same fail-closed rule as the rest of the card: any outcome that is not a
   * usable image returns null and the card falls back to the iMentor mark. A
   * community with no picture answers 404 `{"error":"NotFound"}`, which is the
   * ordinary case, not an error worth surfacing.
   *
   * Reuses `upClient` deliberately — a second axios instance would be a second
   * thing to keep interceptor-free.
   *
   * The destination is pinned by `getAvatarUrl`, which returns null for any
   * URL off the hub origin. That is what keeps the member's JWT from reaching
   * a third-party host, so the null check below is a security guard and not
   * only an input check.
   *
   * @param {string} ref - A sanitized reference from `sanitizeAvatarUrl`.
   * @returns {Promise<Blob|null>}
   */
  async fetchAvatarBlob(ref) {
    const url = getAvatarUrl(ref);
    const token = getAccessToken();
    if (url === null || typeof token !== 'string' || !token) return null;

    const response = await upClient.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    return response?.data ?? null;
  },
};
